import type Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";

/**
 * PR27: the small snake+apple identity mark, in the exact geometry
 * language of `index.html`'s own inline favicon SVG (same 8x8, `rx=2`
 * rounded-square block size, same body/head colors, same apple circle) -
 * just with a longer snake than the favicon's own 2 blocks. The favicon
 * itself is:
 *
 *   <svg viewBox='0 0 32 32'>
 *     <rect width='32' height='32' rx='6' fill='#0f1117'/>
 *     <rect x='6' y='14' width='8' height='8' rx='2' fill='#35c97a'/>
 *     <rect x='14' y='14' width='8' height='8' rx='2' fill='#7dffb3'/>
 *     <circle cx='24' cy='10' r='4' fill='#ff5f6d'/>
 *   </svg>
 *
 * (background square not reproduced here - see below). PR27 human QA
 * asked for the in-app mark specifically (not the favicon file itself,
 * which is unchanged) to read as ~4 connected body blocks instead of 2,
 * so it's recognizably a snake rather than two dots - same block size,
 * same colors (`snakeBody` for every body block, `snakeHead` for the
 * last/head block only, exactly the real gameplay snake's own head/body
 * color split), same apple, same minimal straight-row presentation, just
 * two more body blocks trailing the head. The apple keeps the exact same
 * offset from the head block's own top-right corner (+2 right, -4 up)
 * the favicon uses - only the head block itself moved (further right, to
 * make room for the longer tail), so the apple/head relationship is
 * pixel-identical in spirit, just re-anchored.
 *
 * The background square is deliberately NOT reproduced - it exists in
 * the favicon only for browser-tab contrast, and is the same
 * `GameConfig.colors.background` the app's own scenes already set as
 * their camera background, so drawing it in-app would just be an
 * invisible duplicate square.
 */
const BLOCK = 8;
const BLOCK_RADIUS = 2;
const BODY_BLOCK_COUNT = 3; // tail + 2 more body segments, then 1 head block
const BLOCK_Y = 14;
const APPLE_RADIUS = 4;
const APPLE_Y = 10;
/** Offset from the head block's own top-right corner - identical to the favicon's own (24,10) apple relative to its head block's (22,14) corner. */
const APPLE_OFFSET_X = 2;

export function drawBrandMark(g: Phaser.GameObjects.Graphics, cx: number, cy: number, size: number): void {
  // Local coordinate space sized to fit this (now 4-block) composition's
  // own bounding box - not the original 32x32 favicon viewBox, which
  // only ever fit two blocks. `scale` still maps 1 local unit to the
  // exact same on-screen fraction the favicon's own 32-unit frame would
  // (block size, apple radius, and every proportion below are all still
  // in favicon-native units), so nothing here changes size/proportion -
  // only the frame the composition is centered within grew to match.
  const scale = size / 32;
  const headBlockIndex = BODY_BLOCK_COUNT;
  const headRight = (headBlockIndex + 1) * BLOCK;
  const appleCx = headRight + APPLE_OFFSET_X;

  const minX = 0;
  const maxX = appleCx + APPLE_RADIUS;
  const minY = APPLE_Y - APPLE_RADIUS;
  const maxY = BLOCK_Y + BLOCK;
  const boxCx = (minX + maxX) / 2;
  const boxCy = (minY + maxY) / 2;

  const ox = cx - boxCx * scale;
  const oy = cy - boxCy * scale;
  const px = (x: number) => ox + x * scale;
  const py = (y: number) => oy + y * scale;

  for (let i = 0; i < BODY_BLOCK_COUNT; i++) {
    g.fillStyle(GameConfig.colors.snakeBody, 1);
    g.fillRoundedRect(px(i * BLOCK), py(BLOCK_Y), BLOCK * scale, BLOCK * scale, BLOCK_RADIUS * scale);
  }
  g.fillStyle(GameConfig.colors.snakeHead, 1);
  g.fillRoundedRect(px(headBlockIndex * BLOCK), py(BLOCK_Y), BLOCK * scale, BLOCK * scale, BLOCK_RADIUS * scale);

  g.fillStyle(GameConfig.colors.food, 1);
  g.fillCircle(px(appleCx), py(APPLE_Y), APPLE_RADIUS * scale);
}
